import { useEffect, useState } from 'react'
import { Joyride, CallBackProps, Step, STATUS } from 'react-joyride'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectTutorialProduct, selectTutorialStep, setShouldShowTutorial } from '~/redux/slices/dashboard'
import { useSetUserSettingsMutation } from '~/redux/apis/user-settings-api'
import { CurriculumTutorialUserStatus } from '~/redux/constants/tags.const'
import { delayInMs } from '~/utils'

import { TutorialPlacement, TutorialProducts } from '../types/dashboard.types'
import { TutorialProductSteps } from '../constants/popUpTutorial.const'

import JoyRideTooltip from './JoyRideTooltip'

type ExtendedStep = Step & {
  arrowPlacement: TutorialPlacement
}

type ExtendedCallBackProps = Omit<CallBackProps, 'step'> & {
  step: ExtendedStep
}

const TutorialComponent = (): React.ReactElement => {
  const dispatch = useAppDispatch()
  const [isReady, setIsReady] = useState(false)
  const product = useAppSelector(selectTutorialProduct)
  const tutorialStep = useAppSelector(selectTutorialStep)
  const [setMqOnboardingCompleted] = useSetUserSettingsMutation()

  useEffect(() => {
    const checkClassName = (): void => {
      const element = document.querySelector(TutorialProductSteps[product][tutorialStep]?.target)
      if (element) {
        setIsReady(true)
        clearInterval(intervalId)
      }
    }

    if (product === TutorialProducts.EMPTY || !TutorialProductSteps[product][tutorialStep]) {
      dispatch(setShouldShowTutorial({ product: TutorialProducts.EMPTY, shouldShowTutorial: false }))
    }

    const intervalId = setInterval(checkClassName, 500)

    return () => clearInterval(intervalId)
  }, [dispatch, product, tutorialStep])

  const onFinishTutorial = async (): Promise<void> => {
    if (product === TutorialProducts.CURRICULUM_TUTORIAL_1) {
      await setMqOnboardingCompleted({ curriculumTutorialStatus: CurriculumTutorialUserStatus.CompletedTutorial1 })
      dispatch(setShouldShowTutorial({ product: TutorialProducts.EMPTY, shouldShowTutorial: false }))
    } else if (product === TutorialProducts.CURRICULUM_TUTORIAL_2) {
      await setMqOnboardingCompleted({ curriculumTutorialStatus: CurriculumTutorialUserStatus.CompletedTutorial2 })
      dispatch(setShouldShowTutorial({ product: TutorialProducts.EMPTY, shouldShowTutorial: false }))
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      dispatch(
        setShouldShowTutorial({
          product: TutorialProducts.EMPTY,
          shouldShowTutorial: false,
        }),
      )
    }
  }

  const handleJoyrideCallback = async (data: ExtendedCallBackProps): Promise<void> => {
    const { action, lifecycle, status, index, step } = data
    const { arrowPlacement, target } = step
    if (document && target && action === 'start' && lifecycle === 'init') {
      const ele = document.querySelector((target as string) ?? '')
      if (ele) {
        ele.scrollIntoView({ block: 'center' })
      }
    }
    await delayInMs(200)
    const spotlightElement = document.querySelector('.react-joyride__spotlight') as HTMLElement
    const floaterElement = document.getElementById(`react-joyride-step-${index}`)?.childNodes[0] as HTMLElement
    if (arrowPlacement === TutorialPlacement.BOTTOM && action === 'update' && lifecycle === 'tooltip') {
      if (spotlightElement) {
        const floaterHeight = floaterElement?.offsetHeight
        const topPos =
          spotlightElement.offsetTop >= floaterHeight ? spotlightElement.offsetTop - floaterHeight - 100 : 0
        window.scrollTo({
          top: topPos,
          behavior: 'smooth',
        })
      }
    } else if (arrowPlacement === TutorialPlacement.TOP && action === 'update' && lifecycle === 'tooltip') {
      if (spotlightElement) {
        const navbarHeight = 64
        const floaterArrowHeight = 23
        const spotlightRect = spotlightElement.getBoundingClientRect()
        const floaterRect = floaterElement?.getBoundingClientRect()
        const floaterBottom =
          spotlightRect.bottom + (floaterRect?.height ?? 0) + floaterArrowHeight + window.scrollY + 30
        const viewportAvailable = window.innerHeight - navbarHeight
        const scrollTo = floaterBottom - viewportAvailable
        window.scrollTo({
          top: scrollTo,
          behavior: 'smooth',
        })
      }
    }

    if (status === STATUS.FINISHED) {
      onFinishTutorial()
    }
  }

  if (!isReady) {
    return <></>
  }

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      disableOverlayClose
      disableScrolling
      floaterProps={{
        offset: 0,
        hideArrow: true,
        disableFlip: true,
      }}
      run
      steps={TutorialProductSteps[product]}
      styles={{
        options: {
          zIndex: 1000,
        },
      }}
      tooltipComponent={JoyRideTooltip}
    />
  )
}

export default TutorialComponent
